import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button, cn, Icon, Skeleton } from "@nexus/ui";
import type {
  BigQueryUsageTenantKind,
  BigQueryUsageTenantTotals,
} from "@/features/bigquery-usage/api/bigquery-usage.api";
import { formatPreciseDurationMs } from "@/features/events/lib/event-timing";
import { SortHeader } from "@/features/identity-hub/components/dashboard/shared/table/SortHeader";
import type { SortState } from "@/features/identity-hub/lib/sort";
import { TenantMark } from "@/features/llm-usage/components/TenantMark";
import { formatMicrosAsUsd } from "@/features/llm-usage/lib/money";
import { SECONDARY_LIST_MAX_HEIGHT, STICKY_HEAD_CLASS } from "@/features/llm-usage/lib/table-scroll";
import { sortTableRows, toggleUsageSort, type SortValue } from "@/features/admin/lib/table-sort";
import { formatByteShare, formatBytesCompact, formatBytesExact } from "../lib/bytes";

/** Which tenant the tab is scoped to; `tenantId` is absent for both specials. */
export interface BigQueryTenantSelection {
  kind: BigQueryUsageTenantKind;
  tenantId?: string;
}

interface BigQueryTenantTableProps {
  rows: BigQueryUsageTenantTotals[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isStale: boolean;
  totalBytesBilled: number;
  selected?: BigQueryTenantSelection;
  onSelectTenant: (selection: BigQueryTenantSelection) => void;
}

const HEADER_BASE =
  "border-b border-border px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground";
const HEADER_CLASS = cn(HEADER_BASE, "w-full");
const NUMERIC_HEADER_CLASS = cn(HEADER_BASE, "text-right");
const CELL_CLASS = "px-3 py-2 text-xs text-foreground";
// `whitespace-nowrap` is what keeps a figure on one line. The table lays out
// automatically and the label column takes the slack, so without it a numeric
// column is squeezed to min-content and breaks a value mid-way — "78.4 GiB"
// splitting after the number. The label column truncates, so it yields instead.
const NUMERIC_CELL_CLASS = cn(CELL_CLASS, "text-right tabular-nums whitespace-nowrap");
const SKELETON_ROWS = 3;
const COLUMN_COUNT = 6;
const DASH = "—";
const PERCENT_FRACTION_DIGITS = 1;
const FRACTION_TO_PERCENT = 100;
/** Dense admin rows, so the mark sits a little under the line height. */
const TENANT_MARK_SIZE = 18;

type TenantSortKey =
  | "tenant"
  | "queries"
  | "bytesBilled"
  | "costMicros"
  | "cacheHitRate"
  | "avgDurationMs";

const DEFAULT_SORT: SortState<TenantSortKey> = { key: "bytesBilled", dir: "desc" };
const TEXT_KEYS: ReadonlySet<TenantSortKey> = new Set<TenantSortKey>(["tenant"]);

function rowKey(row: BigQueryUsageTenantTotals): string {
  return `${row.kind}:${row.tenantId ?? ""}`;
}

/** What the cell says, which is also what sorting by tenant sorts on. */
function tenantRowLabel(row: BigQueryUsageTenantTotals, t: TFunction): string {
  if (row.kind === "platform") return t("admin.bigQueryUsage.tenantTable.platform", "Platform");
  if (row.kind === "unattributed") {
    return t("admin.bigQueryUsage.tenantTable.unattributed", "Unattributed");
  }
  return row.tenantName ?? row.tenantId ?? "";
}

function isSameSelection(
  row: BigQueryUsageTenantTotals,
  selected: BigQueryTenantSelection | undefined,
): boolean {
  if (selected == null || selected.kind !== row.kind) return false;
  return (selected.tenantId ?? undefined) === (row.tenantId ?? undefined);
}

/**
 * Three categories, and conflating any two of them misreports the bill:
 *
 * - `tenant` — an org's own queries.
 * - `platform` — work the platform did for itself, which no tenant asked for.
 * - `unattributed` — work whose caller was never plumbed through, so the tenant
 *   is unknown rather than absent.
 *
 * The last two both arrive with a null id, so they are told apart by `kind` and
 * labelled in words. Colour is not carrying any of this: an operator who cannot
 * see the muted hue still reads "Platform" and "no tenant asked for this".
 *
 * Only a named tenant wears a mark. The two specials keep their glyph, since a
 * logo would give a category a brand it does not have, and a tenant whose row is
 * gone gets neither: the only string left to derive initials from is a cuid.
 */
function TenantCategoryCell({
  row,
  isSelected,
  label,
}: {
  row: BigQueryUsageTenantTotals;
  isSelected: boolean;
  label: string;
}) {
  const { t } = useTranslation();

  const note =
    row.kind === "platform"
      ? t("admin.bigQueryUsage.tenantTable.platformNote", "no tenant asked for this")
      : row.kind === "unattributed"
        ? t("admin.bigQueryUsage.tenantTable.unattributedNote", "no tenant recorded")
        : null;

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      aria-label={
        isSelected
          ? t("admin.bigQueryUsage.table.clearFilter", "Stop filtering by {{name}}", {
              name: label,
            })
          : t("admin.bigQueryUsage.table.filterBy", "Show only {{name}}", { name: label })
      }
      className="flex max-w-full items-center gap-2 rounded-sm text-left outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      {row.kind === "tenant" ? (
        <TenantMark
          name={row.tenantName}
          logoUrl={row.logoUrl}
          brandColor={row.brandColor}
          size={TENANT_MARK_SIZE}
        />
      ) : (
        <Icon name={row.kind === "platform" ? "shield" : "info"} size="xs" aria-hidden />
      )}
      <span
        className={cn(
          "truncate",
          // A tenant deleted since the query ran keeps its id, which still
          // traces in the database even though it no longer names anything.
          row.kind === "tenant" && row.tenantName == null && "font-mono text-[11px]",
        )}
      >
        {label}
      </span>
      {note ? <span className="shrink-0 text-[11px] text-muted-foreground">{note}</span> : null}
    </button>
  );
}

/**
 * Bytes billed per tenant, heaviest first, with the platform's own work and the
 * unattributed remainder kept as rows of their own rather than folded in.
 */
export function BigQueryTenantTable({
  rows,
  isLoading,
  isError,
  onRetry,
  isStale,
  totalBytesBilled,
  selected,
  onSelectTenant,
}: BigQueryTenantTableProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [sort, setSort] = useState<SortState<TenantSortKey>>(DEFAULT_SORT);

  // Sorting by tenant sorts by the label on screen, so the two special rows and
  // a dead tenant all fall back to what the cell actually says.
  const sortedRows = useMemo(
    () =>
      sortTableRows(rows, sort, (row, key): SortValue =>
        key === "tenant" ? tenantRowLabel(row, t) : row[key],
      ),
    [rows, sort, t],
  );
  const handleSort = (key: TenantSortKey) =>
    setSort((prev) => toggleUsageSort(prev, key, TEXT_KEYS));

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-md border border-border bg-card">
      <div className="border-b border-border px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {t("admin.bigQueryUsage.tenantTable.title", "By tenant")}
      </div>
      <div className={cn(SECONDARY_LIST_MAX_HEIGHT, "overflow-y-auto")}>
        <table className="w-full border-collapse">
          <caption className="sr-only">
            {t("admin.bigQueryUsage.tenantTable.caption", "BigQuery usage per tenant")}
          </caption>
          <thead className={STICKY_HEAD_CLASS}>
            <tr className="group">
              <SortHeader
                id="tenant"
                label={t("admin.bigQueryUsage.tenantTable.tenant", "Tenant")}
                sort={sort}
                onSort={handleSort}
                className={HEADER_CLASS}
              />
              <SortHeader
                id="queries"
                label={t("admin.bigQueryUsage.table.queries", "Queries")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="bytesBilled"
                label={t("admin.bigQueryUsage.table.bytesBilled", "Bytes billed")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="costMicros"
                label={t("admin.bigQueryUsage.table.cost", "Cost")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="cacheHitRate"
                label={t("admin.bigQueryUsage.table.cache", "Cache")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="avgDurationMs"
                label={t("admin.bigQueryUsage.table.avg", "Avg")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
            </tr>
          </thead>
          <tbody
            className={cn("transition-opacity duration-200", isStale && "opacity-40")}
            aria-busy={isStale || undefined}
          >
            {isError ? (
              <tr>
                <td className={cn(CELL_CLASS, "py-6 text-center")} colSpan={COLUMN_COUNT}>
                  <span className="flex flex-col items-center gap-2 text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Icon name="alert-circle" size="xs" colorVar="--destructive" aria-hidden />
                      {t(
                        "admin.bigQueryUsage.tenantTable.error",
                        "Could not load the tenant breakdown.",
                      )}
                    </span>
                    <Button variant="outline" size="sm" onClick={onRetry}>
                      {t("admin.bigQueryUsage.retry", "Try again")}
                    </Button>
                  </span>
                </td>
              </tr>
            ) : isLoading && rows.length === 0 ? (
              Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <tr key={index} className="border-b border-border last:border-0">
                  <td className={CELL_CLASS} colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-4 w-full rounded-sm bg-foreground/8" />
                  </td>
                </tr>
              ))
            ) : sortedRows.length === 0 ? (
              <tr>
                <td
                  className={cn(CELL_CLASS, "py-6 text-center text-muted-foreground")}
                  colSpan={COLUMN_COUNT}
                >
                  {t("admin.bigQueryUsage.table.empty", "No BigQuery queries in this window")}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const share = formatByteShare(row.bytesBilled, totalBytesBilled, locale);
                const isSelected = isSameSelection(row, selected);

                return (
                  <tr
                    key={rowKey(row)}
                    onClick={() =>
                      onSelectTenant({ kind: row.kind, tenantId: row.tenantId ?? undefined })
                    }
                    className={cn(
                      "cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/40",
                      isSelected && "bg-primary/5 hover:bg-primary/5",
                    )}
                  >
                    <td className={CELL_CLASS}>
                      <TenantCategoryCell row={row} isSelected={isSelected} label={tenantRowLabel(row, t)} />
                    </td>
                    <td className={NUMERIC_CELL_CLASS}>{row.queries.toLocaleString(locale)}</td>
                    <td
                      className={cn(NUMERIC_CELL_CLASS, "font-medium")}
                      title={formatBytesExact(row.bytesBilled, locale)}
                    >
                      <span className="flex items-baseline justify-end gap-1.5">
                        <span>{formatBytesCompact(row.bytesBilled, locale)}</span>
                        {share ? (
                          <span className="text-[11px] font-normal text-muted-foreground">
                            {share}
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td
                      className={cn(
                        NUMERIC_CELL_CLASS,
                        row.costMicros == null && "text-muted-foreground",
                      )}
                      title={
                        row.costMicros == null
                          ? t(
                              "admin.bigQueryUsage.table.costUnpricedTitle",
                              "No price per TB is set. Set it in Admin → Settings → Operations.",
                            )
                          : undefined
                      }
                    >
                      {row.costMicros == null ? DASH : formatMicrosAsUsd(row.costMicros, locale)}
                    </td>
                    <td className={cn(NUMERIC_CELL_CLASS, "text-muted-foreground")}>
                      {row.cacheHitRate == null
                        ? DASH
                        : `${(row.cacheHitRate * FRACTION_TO_PERCENT).toLocaleString(locale, {
                            maximumFractionDigits: PERCENT_FRACTION_DIGITS,
                          })}%`}
                    </td>
                    <td className={cn(NUMERIC_CELL_CLASS, "text-muted-foreground")}>
                      {row.avgDurationMs == null ? DASH : formatPreciseDurationMs(row.avgDurationMs)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
